import { appManager, AppState } from '../manager';

import {
  addElementToIndexedArray,
  arrayToIndexedObject,
} from 'services/indexedObject';

import * as types from './types';
import { notification } from 'antd';

appManager.createSocketListener<types.News[], AppState>(
  'news_received',
  (state, result) => {
    state.sessions.items[state.active_session].last_news_source = 'socket';
    result.forEach(res => addElementToIndexedArray(state.news.items, res));

    state.news.last = arrayToIndexedObject(result);
    state.news.historical_delivery = undefined;
    // this was a side effect, should not sit in reducers
    // result.forEach(news => {
    //   notification.info({
    //     message: news.headline,
    //   });
    // });
  },
);

type HistoricalNewsDeliveryPlanned = {
  session_id: number;
  period_elapsed_days: number;
  news_ids: number[];
};

type HistoricalNewsDeliveryBatch = {
  session_id: number;
  period_elapsed_days: number;
  generation: number;
  batch_index: number;
  news: types.News[];
};

appManager.createSocketListener<HistoricalNewsDeliveryPlanned, AppState>(
  'historical_news_delivery_planned',
  (state, result) => {
    if (result.session_id !== state.active_session) return;
    const session = state.sessions.items[result.session_id];
    if (!session) return;

    const existing =
      state.news.historical_delivery?.session_id === result.session_id
        ? state.news.historical_delivery
        : undefined;
    if (existing && result.period_elapsed_days < existing.period_elapsed_days) {
      return;
    }

    // A legacy fetch may have cached the full period before play began. Clear
    // exactly this plan's IDs; older visible periods remain untouched.
    result.news_ids.forEach(newsId => delete state.news.items[newsId]);
    const plannedIds = new Set(result.news_ids);
    state.news.historical_delivery = {
      session_id: result.session_id,
      period_elapsed_days: result.period_elapsed_days,
      delivery_order: (existing?.delivery_order || []).filter(
        newsId => !plannedIds.has(newsId),
      ),
      current_period_news_ids:
        existing?.period_elapsed_days === result.period_elapsed_days
          ? existing.current_period_news_ids
          : [],
    };
    state.news.last = {};
    session.last_news_source = 'staggered_plan';
  },
);

appManager.createSocketListener<HistoricalNewsDeliveryBatch, AppState>(
  'historical_news_received',
  (state, result) => {
    if (result.session_id !== state.active_session) return;
    const session = state.sessions.items[result.session_id];
    if (!session) return;

    const existing =
      state.news.historical_delivery?.session_id === result.session_id
        ? state.news.historical_delivery
        : undefined;
    const incomingIds = Array.from(new Set(result.news.map(news => news.id)));
    const deliveredIds = new Set(existing?.delivery_order || []);
    const newlyDeliveredIds = incomingIds.filter(
      newsId => !deliveredIds.has(newsId),
    );
    const sessionPeriodElapsedDays = Number.isInteger(session.elapsed_days)
      ? session.elapsed_days
      : result.period_elapsed_days;
    const periodElapsedDays = Math.max(
      sessionPeriodElapsedDays,
      existing?.period_elapsed_days ?? result.period_elapsed_days,
      result.period_elapsed_days,
    );
    const currentPeriodNewsIds =
      existing?.period_elapsed_days === periodElapsedDays
        ? [...existing.current_period_news_ids]
        : [];
    if (result.period_elapsed_days === periodElapsedDays) {
      const currentIds = new Set(currentPeriodNewsIds);
      incomingIds.forEach(newsId => {
        if (!currentIds.has(newsId)) {
          currentIds.add(newsId);
          currentPeriodNewsIds.push(newsId);
        }
      });
    }

    session.last_news_source = 'socket';
    result.news.forEach(news =>
      addElementToIndexedArray(state.news.items, news),
    );
    state.news.historical_delivery = {
      session_id: result.session_id,
      period_elapsed_days: periodElapsedDays,
      delivery_order: [
        ...newlyDeliveredIds,
        ...(existing?.delivery_order || []),
      ],
      current_period_news_ids: currentPeriodNewsIds,
    };
    state.news.last = arrayToIndexedObject(result.news);
  },
);
